import React, { useEffect, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import * as Slider from "@radix-ui/react-slider";
import { ChevronDown, RotateCcw, SlidersHorizontal, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import ClearableControl from "./ClearableControl";
import { formatDuration } from "../utils";

export type FilterRange = [number | null, number | null];
const hasRange = (value: FilterRange) => value.some(bound => bound !== null);
const formatFilterRange = (
  value: FilterRange,
  format: (value: number) => string = String,
): string => {
  const [min, max] = value;
  if (min !== null && max !== null) return `${format(min)}–${format(max)}`;
  if (min !== null) return `≥ ${format(min)}`;
  if (max !== null) return `≤ ${format(max)}`;
  return "";
};

/** Shared entry point for additional filters; page-scope controls remain outside. */
export function FiltersPopover({
  count,
  children,
  onClear,
  align = "end",
}: {
  count: number;
  children: React.ReactNode;
  onClear?: () => void;
  align?: "start" | "end";
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        className={clsx("dropdown-trigger shrink-0", (open || count > 0) && "filter-active")}
      >
        <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
        {t("filters")}
        {count > 0 && (
          <span className="rounded-full bg-info px-1.5 text-[10px] text-white">{count}</span>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          aria-label={t("filters")}
          align={align}
          sideOffset={8}
          collisionPadding={16}
          className="dropdown-menu w-80 max-w-[calc(100vw-32px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-3"
        >
          <div className="mb-3 flex items-center justify-between gap-3 pl-1">
            <span className="font-medium">{t("filters")}</span>
            <Popover.Close
              aria-label={t("close")}
              className="rounded-md p-1 text-text-dim hover:bg-muted-surface hover:text-text focus-visible:outline-info"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </Popover.Close>
          </div>
          <div className="flex flex-col gap-2 [&>button]:w-full">{children}</div>
          {count > 0 && onClear && (
            <div className="mt-3 border-t border-outline pt-2">
              <button
                type="button"
                onClick={onClear}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium text-text-mid hover:bg-muted-surface hover:text-text focus-visible:outline-info"
              >
                <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
                {t("process_directory.clear_filters")}
              </button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function RangeFilter({
  label,
  icon,
  rangeMin,
  rangeMax,
  value,
  onChange,
  duration = false,
}: {
  label: string;
  icon: React.ReactNode;
  rangeMin: number;
  rangeMax: number;
  value: FilterRange;
  onChange: (value: FilterRange) => void;
  duration?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const factor = duration ? 60 : 1;
  const inputValue = (v: number | null) =>
    v === null ? "" : String(Number((v / factor).toFixed(6)));
  const [draft, setDraft] = useState(() => value.map(inputValue));
  const [error, setError] = useState(false);
  const [slider, setSlider] = useState<FilterRange>(value);
  useEffect(() => {
    setDraft(value.map(inputValue));
    setSlider(value);
    setError(false);
  }, [value[0], value[1], open, factor]);
  const format = duration ? formatDuration : String;
  const active = hasRange(value);
  const commitInput = () => {
    const next = draft.map(text =>
      text.trim() === "" ? null : Number(text) * factor,
    ) as FilterRange;
    if (
      next.some(
        v => v !== null && (!Number.isFinite(v) || v < 0 || (!duration && !Number.isInteger(v))),
      ) ||
      (next[0] !== null && next[1] !== null && next[0] > next[1])
    ) {
      setError(true);
      return;
    }
    setError(false);
    const rounded = next.map(v => (v === null ? null : Math.round(v))) as FilterRange;
    if (rounded[0] !== value[0] || rounded[1] !== value[1]) onChange(rounded);
  };
  // Exact inputs may intentionally lie outside the observed data (including no matches).
  // Expand the slider to keep those bounds reachable without changing the constraint.
  const min = Math.min(rangeMin, value[0] ?? rangeMin, value[1] ?? rangeMin);
  const max = Math.max(rangeMax, value[0] ?? rangeMax, value[1] ?? rangeMax);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <ClearableControl
        clearAction={
          active
            ? {
                label: t("filterControls.remove", { filter: label }),
                onClear: () => {
                  onChange([null, null]);
                  setOpen(false);
                },
              }
            : undefined
        }
      >
        <Popover.Trigger
          type="button"
          className={clsx("dropdown-trigger max-w-full", active && "filter-active")}
        >
          <span aria-hidden="true" className="shrink-0 text-text-dim">
            {icon}
          </span>
          <span className="min-w-0 flex-1 truncate">
            {label}:{" "}
            <span className={active ? "text-info" : "text-text-mid"}>
              {active ? formatFilterRange(value, format) : t("filterControls.any")}
            </span>
          </span>
          {!active && <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
        </Popover.Trigger>
      </ClearableControl>
      <Popover.Portal>
        <Popover.Content
          aria-label={label}
          align="start"
          sideOffset={6}
          collisionPadding={16}
          className="dropdown-menu w-80 max-w-[calc(100vw-32px)] p-4"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="font-medium">{label}</span>
            {duration && (
              <span className="text-xs text-text-dim">{t("filterControls.minutes")}</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[t("filterControls.minimum"), t("filterControls.maximum")].map((name, index) => (
              <label key={name} className="min-w-0 space-y-1.5 text-xs text-text-mid">
                <span>{name}</span>
                <input
                  type="number"
                  min={0}
                  step={duration ? "any" : 1}
                  aria-label={duration ? `${name} (${t("filterControls.minutes")})` : name}
                  aria-invalid={error}
                  placeholder={t("filterControls.any")}
                  value={draft[index]}
                  onChange={event =>
                    setDraft(current =>
                      current.map((v, i) => (i === index ? event.target.value : v)),
                    )
                  }
                  onBlur={commitInput}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitInput();
                    }
                  }}
                  className="h-[38px] w-full rounded-lg border border-outline bg-background px-3 text-sm tabular-nums text-text outline-none focus:border-info focus:ring-2 focus:ring-info/20"
                />
              </label>
            ))}
          </div>
          {error && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {t("filterControls.invalid_range")}
            </p>
          )}
          {min < max && (
            <Slider.Root
              className="relative mt-4 flex h-6 touch-none select-none items-center"
              value={[slider[0] ?? min, slider[1] ?? max]}
              min={min}
              max={max}
              step={1}
              onValueChange={next => {
                setSlider(next as FilterRange);
                setDraft(
                  [next[0] === min ? null : next[0], next[1] === max ? null : next[1]].map(
                    inputValue,
                  ),
                );
              }}
              onValueCommit={next =>
                onChange([next[0] === min ? null : next[0], next[1] === max ? null : next[1]])
              }
            >
              <Slider.Track className="relative h-1 grow rounded-full bg-surface-strong">
                <Slider.Range className="absolute h-full rounded-full bg-info" />
              </Slider.Track>
              {[t("filterControls.minimum"), t("filterControls.maximum")].map((name, index) => (
                <Slider.Thumb
                  key={name}
                  aria-label={`${label}: ${name}`}
                  aria-valuetext={format(slider[index] ?? (index ? max : min))}
                  className="block h-4 w-4 cursor-grab rounded-full border-2 border-info bg-surface shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-info/40 active:cursor-grabbing"
                />
              ))}
            </Slider.Root>
          )}
          <p className="mt-2 text-xs text-text-dim">
            {t("filterControls.observed", { range: `${format(rangeMin)}–${format(rangeMax)}` })}
          </p>
          {active && (
            <button
              type="button"
              onClick={() => onChange([null, null])}
              className="mt-3 text-xs font-medium text-info hover:underline focus-visible:outline-info"
            >
              {t("filterControls.reset")}
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
